import { useLazyQuery } from '@apollo/client'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    makeStyles,
    RadioGroup,
    TextField,
    Theme,
    Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import {
    GetPplFamilyAndPtAutoSuggest,
    GetProductFamilyPropertyKeys,
    PPLFamilyAndPTAutoSuggestInput,
    PPLFamilyAndPTAutoSuggestResult,
    ProductFamilyPropertyKeysInput,
    ProductFamilyPropertyKeysResult
} from 'api/graphQL/queries/productFamilyAutosuggest.queries'
import { processEnv } from 'components/configuration/environmentVariables'
import debounce from 'lodash.debounce'
import React, { useMemo } from 'react'
import { toTitleCase } from 'shared/utils/stringUtils'
import { PPLFormData, PPLFormErrors } from './ManageUnmappedProductWizard'
import RadioButton from './RadioButton'

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            width: pxToRem(500),
        },
    },
    dialogContent: {
        padding: theme.spacing(0, 3, 2),
    },
    subtitle: {
        fontSize: pxToRem(22),
        fontWeight: 500,
        marginBottom: theme.spacing(2),
        color: '#304959',
    },
    autocomplete: {
        marginBottom: theme.spacing(2),
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#E0E0E0',
            },
            '&:hover fieldset': {
                borderColor: '#1899D4',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1899D4',
            },
        },
    },
    formLabel: {
        minWidth: pxToRem(200),
        fontSize: pxToRem(14),
        color: '#304959',
        alignItems: 'center',
        marginRight: theme.spacing(1),
        whiteSpace: 'nowrap'
    },
    formControlWrapper: {
        display: "flex",
        width: '100%',
        alignItems: "center",
    },
    productTypeWrapper: {
        marginBottom: theme.spacing(4)
    },
    radioGroup: {
        marginLeft: theme.spacing(1),
    },
    selectDropdown: {
        width: '100%',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#E0E0E0',
            },
            '&:hover fieldset': {
                borderColor: '#1899D4',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1899D4',
            },
            '&.Mui-error fieldset': {
                borderColor: theme.palette.error.main,
            },
        },
    },
    textFieldWrapper: {
        position: 'relative',
        width: '100%',
        '& .MuiFormHelperText-root': {
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: theme.spacing(1),
            marginLeft: 0,
            marginRight: 0,
        },
    },
}))

/**
 * Props for the AddToExistingFamily component
 */
export interface AddToExistingFamilyProps {
    setIsFormValid: (isValid: boolean) => void
    formData: PPLFormData,
    setFormData: React.Dispatch<React.SetStateAction<PPLFormData>>
    errors: PPLFormErrors
    setErrors: React.Dispatch<React.SetStateAction<PPLFormErrors>>
}


/**
 * Dialog for adding an unmapped product to an existing family.
 * 
 * Provides a form to:
 * 1. Select an existing MasterSpec family via autocomplete
 * 2. Choose whether to use an existing product type or create a new one
 * 3. Select/enter the product type information
 * 
 * Uses GraphQL queries to fetch family and product type suggestions.
 * Includes debounced search functionality for better performance.
 * 
 * @param props - The component props
 * @returns The AddToExistingFamily component
 */
const AddToExistingFamily: React.FC<AddToExistingFamilyProps> = ({
    formData,
    setFormData,
    setIsFormValid
}) => {
    const classes = useStyles()

    interface ProductTypeOption {
        contentItemId: string
        elementId: string
        keyText: string
    }

    const [families, setFamilies] = React.useState<string[]>([])
    const [productTypes, setProductTypes] = React.useState<ProductTypeOption[]>([])
    // Controlled so MUI doesn't reset the typed text to the (null) value when productTypes reloads.
    const [keyInputValue, setKeyInputValue] = React.useState('')
    const [isCheckingProductType, setIsCheckingProductType] = React.useState(false)
    const [productTypeExists, setProductTypeExists] = React.useState(false)

    // Ref to track the latest newProductTypeName value, avoiding stale closures in useLazyQuery onCompleted
    const latestNewProductTypeName = React.useRef<string>(formData.newProductTypeName || '')

    const formattedFamilies = useMemo(() => {
        return families.map(family => {
            const tempFamily = family.split("|")
            const familyNumber = tempFamily[0]
            const familyName = tempFamily[1]
            return { key: familyNumber, value: `${familyNumber} - ${familyName}` }
        })
    }, [families])

    // Lazy queries for autocomplete
    const [searchFamilies, { loading: familiesLoading }] = useLazyQuery<PPLFamilyAndPTAutoSuggestResult>(
        GetPplFamilyAndPtAutoSuggest,
        {
            onCompleted: (data) => {
                setFamilies(data?.getPPLFamilyAndPTAutoSuggest?.result || [])
            },
            onError: (error) => {
                setFamilies([])
            }
        }
    )

    const [searchProductTypes, { loading: productTypesLoading }] = useLazyQuery<ProductFamilyPropertyKeysResult>(
        GetProductFamilyPropertyKeys,
        {
            onCompleted: (data) => {
                const propertyKeys = (data?.getProductFamilyPropertyKeys?.productFamilyPropertyKeys || [])
                    .filter((key) => key.keyText?.trim())
                setProductTypes(propertyKeys)
            },
            onError: (error) => {
                setProductTypes([])
            }
        }
    )

    const [validateProductType] = useLazyQuery<PPLFamilyAndPTAutoSuggestResult>(
        GetPplFamilyAndPtAutoSuggest,
        {
            onCompleted: (data) => {
                const results = data?.getPPLFamilyAndPTAutoSuggest?.result || []
                const productTypeNames = results.map(result => (result || '').trim().toLocaleLowerCase())
                // Use ref to get the latest value — formData in closure may be stale
                const newProductTypeName = (latestNewProductTypeName.current || '').trim().toLocaleLowerCase()
                const exists = productTypeNames.includes(newProductTypeName)
                setProductTypeExists(exists)
                setIsCheckingProductType(false)
            },
            onError: (error) => {
                setProductTypeExists(false)
                setIsCheckingProductType(false)
            }
        }
    )

    // Debounced search functions
    const debouncedSearchFamilies = React.useMemo(
        () => debounce((searchText: string) => {
            searchFamilies({
                variables: {
                    input: {
                        searchText,
                        maxResults: 100,
                        searchProductTypes: false,
                    } as PPLFamilyAndPTAutoSuggestInput
                }
            })
        }, 300),
        [searchFamilies]
    )

    const debouncedSearchProductTypes = React.useMemo(
        () => debounce((familyNumber: string, familyName: string) => {
            if (familyNumber && familyName) {
                searchProductTypes({
                    variables: {
                        input: {
                            familyNumber,
                            familyName,
                        } as ProductFamilyPropertyKeysInput
                    }
                })
            }
        }, 300),
        [searchProductTypes]
    )

    const debouncedValidateProductType = React.useMemo(
        () => debounce((searchText: string, familyName: string) => {
            if (familyName && searchText.trim()) {
                setIsCheckingProductType(true)
                validateProductType({
                    variables: {
                        input: {
                            searchText,
                            maxResults: 1,
                            searchProductTypes: true,
                            productFamilyName: familyName,
                        } as PPLFamilyAndPTAutoSuggestInput
                    }
                })
            } else {
                setIsCheckingProductType(false)
                setProductTypeExists(false)
            }
        }, 200),
        [validateProductType]
    )

    const resetValidationState = () => {
        setIsCheckingProductType(false)
        setProductTypeExists(false)
        debouncedValidateProductType.cancel()
    }

    const handleFamilyChange = (event: any, newValue: Record<string, string> | null) => {
        setFormData(prev => ({
            ...prev,
            selectedFamily: newValue || null,
            existingProductType: '',
            contentItemId: '',
            elementId: '',
        }))
        setKeyInputValue('')

        // Reset validation state when family changes
        resetValidationState()

        if (formData.selectedOption === 'existing') {
            // Existing keys: just (re)load the new family's full list to filter client-side
            // slice/join so names containing " - " (e.g. "Acme - West") aren't truncated
            const familyName = newValue?.value?.split(' - ').slice(1).join(' - ')
            if (newValue?.key && familyName) {
                debouncedSearchProductTypes(newValue.key, familyName)
            }
        } else if (formData.newProductTypeName?.trim() && newValue?.key) {
            // New product type: re-validate the entered name against the new family
            debouncedValidateProductType(formData.newProductTypeName, newValue.key)
        }
    }

    const handleProductTypeOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as 'existing' | 'new'
        setFormData(prev => ({
            ...prev,
            selectedOption: value,
            existingProductType: '',
            newProductTypeName: '',
            contentItemId: '',
            elementId: '',
        }))
        setKeyInputValue('')

        // Reset validation state when switching options
        resetValidationState()
    }

    const handleExistingProductTypeChange = (event: any, newValue: ProductTypeOption | null) => {
        setFormData(prev => ({
            ...prev,
            existingProductType: newValue?.keyText || '',
            contentItemId: newValue?.contentItemId || '',
            elementId: newValue?.elementId || '',
        }))
        // onInputChange ignores 'reset', so set the displayed input to the selection here
        setKeyInputValue(newValue?.keyText || '')
    }

    const handleNewProductTypeNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value
        // Keep ref up to date so onCompleted closure always reads the latest value
        latestNewProductTypeName.current = newValue
        setFormData(prev => {
            const updated = { ...prev, newProductTypeName: newValue }
            // Trigger validation with the latest family value to avoid race conditions
            if (updated.selectedFamily?.key) {
                debouncedValidateProductType(newValue, updated.selectedFamily.key)
            }
            return updated
        })
    }

    const resetForms = () => {
        latestNewProductTypeName.current = ''
        setFormData(prev => ({
            ...prev,
            selectedFamily: null,
            selectedOption: 'new',
            existingProductType: '',
            newProductTypeName: '',
            contentItemId: '',
            elementId: '',
        }))
        setKeyInputValue('')
        setFamilies([])
        setProductTypes([])
        resetValidationState()
        debouncedSearchFamilies.cancel()
        debouncedSearchProductTypes.cancel()
    }

    const isFormValid = React.useMemo(() => {
        // Family selection is always required
        if (!formData.selectedFamily) return false

        if (formData.selectedOption === 'existing') {
            // For existing product type, need both family and existing product type
            return Boolean(formData.selectedFamily && formData.existingProductType)
        } else {
            // For new product type, need family, new product type name,
            // validation complete, and no duplicates
            return Boolean(
                formData.selectedFamily &&
                formData.newProductTypeName?.trim() &&
                !isCheckingProductType &&
                !productTypeExists
            )
        }
    }, [formData, isCheckingProductType, productTypeExists])

    React.useEffect(() => {
        // Initialize form on mount
        resetForms()

        // Cleanup debounced functions on unmount
        return () => {
            debouncedSearchFamilies.cancel()
            debouncedSearchProductTypes.cancel()
            debouncedValidateProductType.cancel()
        }
    }, [debouncedSearchFamilies, debouncedSearchProductTypes, debouncedValidateProductType])

    React.useEffect(() => {
        setIsFormValid(isFormValid)
    }, [isFormValid, setIsFormValid])

    return (
        <>
            <div className={classes.subtitle}>
                Add to Existing Family
            </div>
            <Typography className={classes.formControlWrapper}>
                <Typography className={classes.formLabel}>Select Existing Family:</Typography>
                <Autocomplete
                    options={formattedFamilies}
                    value={formData.selectedFamily}
                    onChange={handleFamilyChange}
                    onInputChange={(event, newInputValue) => {
                        debouncedSearchFamilies(newInputValue)
                    }}
                    onFocus={() => {
                        debouncedSearchFamilies(formData.selectedFamily?.value || '')
                    }}
                    loading={familiesLoading}
                    className={classes.selectDropdown}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            fullWidth
                        />
                    )}
                    getOptionLabel={
                        option => toTitleCase(option.value)
                    }
                />
            </Typography>

            <RadioGroup
                value={formData.selectedOption}
                onChange={handleProductTypeOptionChange}
                className={classes.radioGroup}
            >
                {processEnv.REACT_APP_EnablePPLFeature2a === "1" &&
                    <RadioButton
                        value="existing"
                        label="Select an existing Key Value"
                    />
                }
                <RadioButton
                    value="new"
                    label="Create a new product type for this listing"
                />
            </RadioGroup>

            <Typography className={classes.productTypeWrapper}>
                {formData.selectedOption === 'existing' && (
                    <Typography className={classes.formControlWrapper}>
                        <Typography className={classes.formLabel}>Key:</Typography>
                        <Autocomplete
                            options={productTypes}
                            value={productTypes.find(pt => pt.keyText === formData.existingProductType) || null}
                            inputValue={keyInputValue}
                            onChange={handleExistingProductTypeChange}
                            onInputChange={(_, newInputValue, reason) => {
                                // Ignore MUI's internal 'reset' (fired on value change / blur /
                                // options refresh) so the typed text isn't wiped after the API call.
                                if (reason === 'reset') return
                                // Keys load once; typing filters the in-memory list client-side (no per-keystroke API call).
                                setKeyInputValue(newInputValue)
                            }}
                            onFocus={() => {
                                const familyNumber = formData.selectedFamily?.key
                                const familyName = formData.selectedFamily?.value?.split(' - ').slice(1).join(' - ')
                                if (familyNumber && familyName) {
                                    debouncedSearchProductTypes(familyNumber, familyName)
                                }
                            }}
                            getOptionLabel={(option) => option.keyText}
                            getOptionSelected={(option, value) => option.keyText === value.keyText}
                            loading={productTypesLoading}
                            className={classes.selectDropdown}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    size='small'
                                    fullWidth
                                />
                            )}
                        />
                    </Typography>
                )}

                {formData.selectedOption === 'new' && (
                    <Typography className={classes.formControlWrapper}>
                        <Typography className={classes.formLabel}>Product Type Name:</Typography>
                        <div className={classes.textFieldWrapper}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                value={formData.newProductTypeName}
                                onChange={handleNewProductTypeNameChange}
                                className={classes.selectDropdown}
                                size="small"
                                error={productTypeExists}
                                helperText={productTypeExists ? 'A Product Type already exists with that name. Please revise.' : ''}
                            />
                        </div>
                    </Typography>
                )}
            </Typography>
        </>
    )
}

export default AddToExistingFamily